/**
 * Level Completion Service
 *
 * Handles saving and querying level completions (raw data).
 * Local-first with background Firestore sync via UnifiedBatchSyncService.
 *
 * Storage tiers:
 *   - In-memory cache (Map)  — synchronous reads
 *   - IndexedDB              — durable local persistence (no quota pressure)
 *   - Firestore              — source of truth across devices
 */

import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { LevelCompletion } from '../types/levelCompletion';
import { firestoreService } from './database/FirestoreService';
import { indexedDBService } from './database/IndexedDBService';
import { batchSyncService } from './UnifiedBatchSyncService';

// Legacy localStorage prefix kept ONLY for one-time migration of pre-IDB
// caches at hydrate() time. After migration, no level_completion_* keys remain.
const LEGACY_STORAGE_KEY_PREFIX = 'level_completion_';

function cacheKey(userId: string, completionId: string): string {
  return `${userId}_${completionId}`;
}

/**
 * Generate unique completion ID
 *
 * Every level completion gets a UNIQUE ID, even repeats: all attempts
 * are saved for stats / streak / daily activity tracking.
 *
 * Format: comp_{timestamp}_{random}
 */
function generateCompletionId(): string {
  return `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

class LevelCompletionServiceClass {
  // In-memory cache (synchronous read tier). Hydrated from IDB on login.
  private cache = new Map<string, LevelCompletion>();
  private hydrated = new Set<string>();
  private hydrationPromises = new Map<string, Promise<void>>();

  /**
   * Load completions from IDB into the in-memory cache, migrate any leftover
   * legacy `level_completion_*` localStorage keys, and mark the user hydrated.
   * Awaited by AuthContext before unblocking the UI — synchronous query
   * methods (queryCompletions, getRecentCompletions) depend on warm cache.
   */
  async hydrate(userId: string): Promise<void> {
    if (this.hydrated.has(userId)) return;
    const inFlight = this.hydrationPromises.get(userId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      try {
        const rows = await indexedDBService.getLevelCompletionsForUser(userId);
        for (const row of rows) {
          const completion = this.reviveCompletion(row.data);
          this.cache.set(cacheKey(userId, row.completionId), completion);
        }
        if (rows.length > 0) {
          console.log(`💧 Hydrated ${rows.length} level completions from IDB for ${userId}`);
        }
        await this.migrateLegacyLocalStorage(userId);
        this.hydrated.add(userId);
      } finally {
        this.hydrationPromises.delete(userId);
      }
    })();

    this.hydrationPromises.set(userId, promise);
    return promise;
  }

  private async migrateLegacyLocalStorage(userId: string): Promise<void> {
    const prefix = `${LEGACY_STORAGE_KEY_PREFIX}${userId}_`;
    const candidates: Array<{ lsKey: string; completionId: string }> = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          candidates.push({ lsKey: k, completionId: k.slice(prefix.length) });
        }
      }
    } catch {
      return;
    }
    if (candidates.length === 0) return;

    let migrated = 0;
    for (const { lsKey, completionId } of candidates) {
      const ckey = cacheKey(userId, completionId);
      if (this.cache.has(ckey)) {
        try { localStorage.removeItem(lsKey); } catch {}
        continue;
      }
      try {
        const raw = localStorage.getItem(lsKey);
        if (!raw) { localStorage.removeItem(lsKey); continue; }
        const completion = this.reviveCompletion(JSON.parse(raw));
        await indexedDBService.putLevelCompletion(userId, completionId, completion);
        this.cache.set(ckey, completion);
        localStorage.removeItem(lsKey);
        migrated++;
      } catch (err) {
        console.warn(`Could not migrate legacy completion ${lsKey}:`, err);
      }
    }
    if (migrated > 0) {
      console.log(`🔁 Migrated ${migrated} legacy localStorage completion(s) → IDB for ${userId}`);
    }
  }

  /**
   * Date revival + legacy field defaults. Idempotent. Run once at hydrate /
   * Firestore-pull time so reads stay cheap.
   */
  private reviveCompletion(raw: any): LevelCompletion {
    const completion = raw;
    if (completion.timestamp && !(completion.timestamp instanceof Date)) {
      completion.timestamp = new Date(completion.timestamp);
    }
    if (!completion.errorMode) {
      completion.errorMode = 'strict';
    }
    return completion;
  }

  /**
   * Save level completion: cache (sync) + IDB (durable, fire-and-forget)
   * + queue for background Firestore sync.
   */
  async saveLevelCompletion(userId: string, completion: LevelCompletion): Promise<void> {
    try {
      if (!completion.id) {
        completion.id = generateCompletionId();
      }

      this.cache.set(cacheKey(userId, completion.id), completion);
      indexedDBService.putLevelCompletion(userId, completion.id, completion).catch(err => {
        console.warn(`IDB write failed for completion ${completion.id}:`, err);
      });

      console.log(`💾 Saved level completion: ${completion.id}`);

      batchSyncService.queueLevelCompletion(userId, completion.id);
    } catch (error) {
      console.error('Error saving level completion:', error);
      throw error;
    }
  }

  /**
   * Get level completion (synchronous, from cache).
   */
  getLevelCompletion(userId: string, completionId: string): LevelCompletion | null {
    const completion = this.cache.get(cacheKey(userId, completionId));
    if (!completion) return null;
    if ((completion as any).deleted) return null;
    return completion;
  }

  /**
   * Iterate cache for a user, optionally filtered by predicate.
   * Filters out soft-deleted completions unless `includeDeleted` is true.
   */
  private collectCompletions(
    userId: string,
    predicate: (c: LevelCompletion) => boolean = () => true,
    includeDeleted = false,
  ): LevelCompletion[] {
    const prefix = `${userId}_`;
    const out: LevelCompletion[] = [];
    for (const [key, completion] of this.cache) {
      if (!key.startsWith(prefix)) continue;
      if (!includeDeleted && (completion as any).deleted) continue;
      if (!predicate(completion)) continue;
      out.push(completion);
    }
    return out;
  }

  /**
   * Get all completions for a user (including their full history).
   */
  async getAllCompletions(userId: string): Promise<LevelCompletion[]> {
    return this.collectCompletions(userId);
  }

  /**
   * Mark completions matching `predicate` as deleted, write through to IDB,
   * return count marked. Used by per-keyboard / per-language / per-session
   * deletion flows.
   */
  private markDeletedWhere(
    userId: string,
    predicate: (c: LevelCompletion) => boolean,
  ): number {
    let marked = 0;
    const prefix = `${userId}_`;
    for (const [key, completion] of this.cache) {
      if (!key.startsWith(prefix)) continue;
      if ((completion as any).deleted) continue;
      if (!predicate(completion)) continue;
      (completion as any).deleted = true;
      (completion as any).deletedAt = new Date();
      const completionId = key.slice(prefix.length);
      indexedDBService.putLevelCompletion(userId, completionId, completion).catch(err => {
        console.warn(`IDB write failed marking deleted ${completionId}:`, err);
      });
      marked++;
    }
    return marked;
  }

  markLevelCompletionsAsDeletedByKeyboard(userId: string, keyboardId: string): number {
    const n = this.markDeletedWhere(userId, c => c.keyboardId === keyboardId);
    console.log(`🗑️ Marked ${n} level completions as deleted for keyboard ${keyboardId}`);
    return n;
  }

  markLevelCompletionsAsDeletedByLanguage(userId: string, language: string): number {
    const n = this.markDeletedWhere(userId, c => c.language === language);
    console.log(`🗑️ Marked ${n} level completions as deleted for language ${language}`);
    return n;
  }

  markLevelCompletionsAsDeletedBySession(userId: string, language: string, keyboardId: string): number {
    const n = this.markDeletedWhere(
      userId,
      c => c.language === language && c.keyboardId === keyboardId,
    );
    console.log(`🗑️ Marked ${n} level completions as deleted for session ${language}/${keyboardId}`);
    return n;
  }

  /**
   * Get recent completions, sorted newest-first.
   */
  getRecentCompletions(userId: string, limit: number = 100): LevelCompletion[] {
    const completions = this.collectCompletions(userId);
    completions.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return completions.slice(0, limit);
  }

  /**
   * Force immediate sync (for tournaments or explicit user action)
   */
  async forceSyncNow(userId: string): Promise<void> {
    await batchSyncService.flushImmediately(userId);
  }

  /**
   * Query completions with filters (cache-backed, synchronous).
   */
  queryCompletions(
    userId: string,
    filters: {
      mode?: string;
      language?: string;
      keyboardId?: string;
      startDate?: Date;
      endDate?: Date;
    } = {},
  ): LevelCompletion[] {
    return this.collectCompletions(userId, completion => {
      if (filters.mode && completion.mode !== filters.mode) return false;
      if (filters.language && completion.language !== filters.language) return false;
      if (filters.keyboardId && completion.keyboardId !== filters.keyboardId) return false;
      if (filters.startDate && completion.timestamp < filters.startDate) return false;
      if (filters.endDate && completion.timestamp > filters.endDate) return false;
      return true;
    });
  }

  /**
   * Get today's completions (for streak calculation)
   */
  getTodayCompletions(userId: string, mode?: string): LevelCompletion[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return this.queryCompletions(userId, {
      startDate: today,
      endDate: tomorrow,
      mode,
    });
  }

  /**
   * Sync level completions FROM Firestore into the local cache + IDB.
   * Used at login on a fresh origin so synchronous queryCompletions reads
   * see the full history. Skips entries already cached locally (they may
   * have unsynced edits).
   */
  async syncFromFirestore(userId: string): Promise<{ synced: number; skipped: number }> {
    await this.hydrate(userId);
    try {
      const remote = await firestoreService.getLevelCompletions(userId);
      if (remote.length === 0) {
        return { synced: 0, skipped: 0 };
      }

      let synced = 0;
      let skipped = 0;
      for (const completion of remote) {
        if (!completion.id) {
          skipped++;
          continue;
        }
        const ckey = cacheKey(userId, completion.id);
        if (this.cache.has(ckey)) {
          skipped++;
          continue;
        }
        const revived = this.reviveCompletion(completion);
        this.cache.set(ckey, revived);
        indexedDBService.putLevelCompletion(userId, completion.id, revived).catch(err => {
          console.warn(`IDB write failed during completion sync ${completion.id}:`, err);
        });
        synced++;
      }

      if (synced > 0) {
        console.log(`📥 syncFromFirestore: pulled ${synced} level completion(s) from Firestore (${skipped} already cached)`);
        window.dispatchEvent(new CustomEvent('progressUpdated'));
      }
      return { synced, skipped };
    } catch (error) {
      console.error('Error syncing level completions from Firestore:', error);
      return { synced: 0, skipped: 0 };
    }
  }

  /**
   * One-time backfill: push every locally-cached completion to Firestore.
   * Catches completions whose batch-sync queue was lost when the tab closed
   * before the 5-second debounce fired. Idempotent via per-user marker.
   */
  async backfillToFirestore(userId: string): Promise<void> {
    const doneKey = `lc_backfill_done_${userId}`;
    if (localStorage.getItem(doneKey) === '1') return;
    await this.hydrate(userId);

    const local = this.getRecentCompletions(userId, 10000);
    if (local.length === 0) {
      localStorage.setItem(doneKey, '1');
      return;
    }

    let remoteIds: Set<string>;
    try {
      const remote = await firestoreService.getLevelCompletions(userId);
      remoteIds = new Set(remote.map(c => c.id).filter((x): x is string => !!x));
    } catch (error) {
      console.error('Could not read Firestore completions for backfill (will retry next login):', error);
      return;
    }

    const toWrite = local.filter(c => c.id && !remoteIds.has(c.id));
    if (toWrite.length === 0) {
      localStorage.setItem(doneKey, '1');
      return;
    }

    console.log(`📤 Backfilling ${toWrite.length} level completion(s) to Firestore (per-doc)...`);

    let succeeded = 0;
    const failures: Array<{ id: string; error: unknown }> = [];

    for (const completion of toWrite) {
      try {
        const docRef = doc(db, 'users', userId, 'levelCompletions', completion.id);
        await setDoc(docRef, {
          ...completion,
          timestamp: completion.timestamp instanceof Date
            ? Timestamp.fromDate(completion.timestamp)
            : Timestamp.fromDate(new Date(completion.timestamp)),
        });
        succeeded++;
        if (succeeded % 50 === 0) {
          console.log(`  …${succeeded}/${toWrite.length} completions pushed`);
        }
      } catch (error) {
        failures.push({ id: completion.id, error });
      }
    }

    if (failures.length === 0) {
      localStorage.setItem(doneKey, '1');
      console.log(`✅ Level-completion backfill complete (${succeeded} completions)`);
    } else {
      console.warn(
        `⚠️ Level-completion backfill: ${succeeded} succeeded, ${failures.length} failed. ` +
        `Will retry on next login. First failure:`,
        failures[0],
      );
    }
  }

  /**
   * Clear all completions for user (for testing/reset). Drops cache, IDB,
   * and any leftover legacy localStorage keys.
   */
  clearAllCompletions(userId: string): void {
    const prefix = `${userId}_`;
    for (const key of Array.from(this.cache.keys())) {
      if (key.startsWith(prefix)) this.cache.delete(key);
    }
    indexedDBService.deleteAllLevelCompletionsForUser(userId).catch(err => {
      console.warn('IDB clear failed:', err);
    });
    const lsPrefix = `${LEGACY_STORAGE_KEY_PREFIX}${userId}_`;
    try {
      const drop: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(lsPrefix)) drop.push(k);
      }
      drop.forEach(k => localStorage.removeItem(k));
    } catch {}
    this.hydrated.delete(userId);
    batchSyncService.clearPendingSyncs(userId);
    console.log(`🗑️ Cleared all level completions for user ${userId}`);
  }
}

export const levelCompletionService = new LevelCompletionServiceClass();
